import React, { useRef, useState, useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Header, Footer } from '../components-static/Common';
import moment from 'moment';
import { io } from 'socket.io-client';
import '../styles/turnstile-monitoring.scss';
import u from '../utilities/Utilities';

const TurnstileMonitoring = ({ title }) => {
  const socketRef = useRef(null);
  const intervalRef = useRef(null);
  const demoIntervalRef = useRef(null);
  const westTurnstileRef = useRef(null);
  const eastTurnstileRef = useRef(null);
  const mobileRowRef = useRef(null);
  const mobileContainerRef = useRef(null);
  const mobileHeaderRef = useRef(null);
  const mobileFooterRef = useRef(null);
  const monitoringContainerRef = useRef(null);
  const [towerScales, setTowerScales] = useState({ west: 1, east: 1 });
  const [mobileRowScale, setMobileRowScale] = useState(1);
  const [mobileRowHeight, setMobileRowHeight] = useState(null);
  // Page container box size, captured alongside the row scale below so both stay in sync.
  const [mobilePageSize, setMobilePageSize] = useState(null);

  // State
  const [initialized, setInitialized] = useState(false);
  const [westLabel, setWestLabel] = useState('');
  const [eastLabel, setEastLabel] = useState('');
  const [turnstiles, setTurnstiles] = useState([]);
  const [error, setError] = useState('Not connected to server');
  const [demoMode, setDemoMode] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isPortraitDevice, setIsPortraitDevice] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);
  const [windowHeight, setWindowHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 1080);
  const [sessionID, setSessionID] = useState('');
  const [successReset, setSuccessReset] = useState(3);
  const [failReset, setFailReset] = useState(10);
  const [mobileWing, setMobileWing] = useState('west');

  // Handle window resize for responsive breakpoint based on aspect ratio
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isDesktopView = () => {
      //const aspectRatio = window.innerWidth / window.innerHeight;
      return window.innerWidth > 1200;
    };

    setIsMobileView(!isDesktopView());
    setIsPortraitDevice(window.innerHeight > window.innerWidth);
    setWindowWidth(window.innerWidth);
    setWindowHeight(window.innerHeight);

    // Skip no-op updates so unrelated re-renders don't cascade into the mobile row scale effect.
    const handleResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setIsMobileView((prev) => {
        const next = w <= 1200;
        return prev === next ? prev : next;
      });
      setIsPortraitDevice((prev) => {
        const next = h > w;
        return prev === next ? prev : next;
      });
      setWindowWidth((prev) => (prev === w ? prev : w));
      setWindowHeight((prev) => (prev === h ? prev : h));
    };

    // Debounce scroll (toolbar show/hide fires it repeatedly mid-animation).
    let scrollDebounce;
    const handleScroll = () => {
      clearTimeout(scrollDebounce);
      scrollDebounce = setTimeout(handleResize, 150);
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollDebounce);
    };
  }, []);

  // Scale tower turnstile rows to always fit in a single row (uniform scale)
  useEffect(() => {
    if (isMobileView) return;
    const calcScale = (ref) => {
      if (!ref.current) return 1;
      const parent = ref.current.parentElement;
      if (!parent) return 1;
      const available = parent.clientWidth;
      const content = ref.current.scrollWidth;
      return content > available ? available / content : 1;
    };
    const scale = Math.min(calcScale(westTurnstileRef), calcScale(eastTurnstileRef));
    setTowerScales({ west: scale, east: scale });
  }, [windowWidth, turnstiles, isMobileView]);

  useEffect(() => {
    if (!isMobileView) return;
    const calcScale = () => {
      if (!mobileRowRef.current || !mobileContainerRef.current) return { scale: 1, height: null };
      const containerStyle = getComputedStyle(mobileContainerRef.current);
      const containerPaddingX = parseFloat(containerStyle.paddingLeft) + parseFloat(containerStyle.paddingRight);
      const containerPadding = parseFloat(containerStyle.paddingTop) + parseFloat(containerStyle.paddingBottom);
      const widthSafetyMargin = 20;
      const availableWidth = mobileContainerRef.current.clientWidth - containerPaddingX - widthSafetyMargin;
      const contentWidth = mobileRowRef.current.scrollWidth;
      const widthScale = contentWidth > availableWidth ? availableWidth / contentWidth : 1;

      const headerHeight = mobileHeaderRef.current ? mobileHeaderRef.current.offsetHeight : 0;
      const footerHeight = mobileFooterRef.current ? mobileFooterRef.current.offsetHeight : 0;
      const containerGaps = 20; // matches .mobile-container gap x2
      const safetyMargin = 30;
      const liveWidth = window.innerWidth;
      const liveHeight = window.innerHeight;
      const livePortrait = liveHeight > liveWidth;
      const monitoringContainerHeight = livePortrait ? liveWidth : liveHeight;
      const monitoringContainerWidth = livePortrait ? liveHeight : liveWidth;
      const availableHeight = monitoringContainerHeight - headerHeight - footerHeight
        - containerPadding - containerGaps - safetyMargin;
      const contentHeight = mobileRowRef.current.scrollHeight;
      const heightScale = contentHeight > availableHeight ? availableHeight / contentHeight : 1;

      const scale = Math.min(widthScale, heightScale);
      const height = contentHeight * scale;
      return { scale, height, pageHeight: monitoringContainerHeight, pageWidth: monitoringContainerWidth };
    };
    const settleTimeout = setTimeout(() => {
      const { scale, height, pageHeight, pageWidth } = calcScale();
      setMobileRowScale(scale);
      setMobileRowHeight(height);
      setMobilePageSize({ height: pageHeight, width: pageWidth });
    }, 400);
    return () => clearTimeout(settleTimeout);
    // Recalculate on view/wing/orientation changes
  }, [isMobileView, mobileWing, isPortraitDevice]);

  // Initialize
  useEffect(() => {
    setupSockets();

    const interval = setInterval(() => {
      setIdle();
    }, 500);
    intervalRef.current = interval;
    setInitialized(true);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (demoIntervalRef.current) clearInterval(demoIntervalRef.current);
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, []);

  const setIdle = () => {
    setTurnstiles((prevTurnstiles) => {
      let changed = false;
      const next = prevTurnstiles.map((item) => {
        const successEnded =
          moment().diff(item.dateTime, 'seconds') >= successReset &&
          item.result === 'Success';
        const failureEnded =
          moment().diff(item.dateTime, 'seconds') >= failReset &&
          (item.result === 'Failed' || item.result === 'Passback');

        if (successEnded || failureEnded) {
          changed = true;
          return { ...item, action: 'Idle', result: 'None' };
        }
        return item;
      });
      return changed ? next : prevTurnstiles; // keep same reference when unchanged
    });
  };

  const setupSockets = () => {
    const ipConfig = require('../../config.json');
    const socket = io(ipConfig.socketIP, {
      reconnectionDelayMax: 5000,
      withCredentials: true,
      extraHeaders: {
        'csrf-token': 'turnstile-monitoring',
      },
      transports: ['websocket'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setSessionID(socket.id);
      setError('');
    });

    socket.on('disconnect', () => {
      setError('Not connected to server');
    });

    socket.on('set-base-state', (content) => {
      const updatedTurnstiles = content.turnstiles.map((item) => ({
        ...item,
        dateTime: moment().format('YYYY-MM-DDTHH:mm:ss'),
      }));

      setTurnstiles(updatedTurnstiles);
      setEastLabel(content.eastTowerLabel);
      setWestLabel(content.westTowerLabel);
      setSuccessReset(content.successReset);
      setFailReset(content.failReset);
    });

    socket.on('update-turnstile', (turnstile) => {
      setTurnstiles((prevTurnstiles) => {
        const updated = prevTurnstiles.map((item) => {
          if (item.id === turnstile.id) {
            return {
              ...item,
              ...turnstile,
              dateTime: moment().format('YYYY-MM-DDTHH:mm:ss'),
            };
          }
          return item;
        });
        return updated;
      });
    });
  };

  const onToggleDemo = () => {
    const newMode = !demoMode;
    if (newMode) {
      const interval = setInterval(() => {
        if (socketRef.current) {
          socketRef.current.emit('demo-update-turnstile', {
            socketId: socketRef.current.id,
          });
        }
      }, 1000);
      demoIntervalRef.current = interval;
    } else {
      if (demoIntervalRef.current) {
        clearInterval(demoIntervalRef.current);
      }
    }
    setDemoMode(newMode);
  };

  const restartTurnstile = (id) => {
    if (socketRef.current) {
      socketRef.current.emit('restart-turnstile', { id });
    }
  };

  if (!initialized) {
    return <div />;
  }

  const westTurnstiles = turnstiles.filter((t) => t.location === 'west');
  const eastTurnstiles = turnstiles.filter((t) => t.location === 'east');

  return (
    <HelmetProvider>
      <div
        className={`monitoring-page-container ${isMobileView ? 'mobile-landscape-lock' : ''}`}
        style={isMobileView && mobilePageSize ? { height: mobilePageSize.height, width: mobilePageSize.width } : undefined}
      >
        <Helmet>
          <title>{title}</title>
          <meta name="viewport" content="width=device-width,initial-scale=1" />
        </Helmet>

        {!isMobileView && <Header type="logo" />}

        <div className="monitoring-container" ref={monitoringContainerRef}>
          {error && (
            <div
              className="page-error"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                textAlign: 'center',
              }}
            >
              {error}
            </div>
          )}

          {isMobileView ? (
            <MobileMonitoringView
              westTurnstiles={westTurnstiles}
              eastTurnstiles={eastTurnstiles}
              wing={mobileWing}
              onWingChange={setMobileWing}
              rowScale={mobileRowScale}
              rowHeight={mobileRowHeight}
              rowRef={mobileRowRef}
              containerRef={mobileContainerRef}
              headerRef={mobileHeaderRef}
              footerRef={mobileFooterRef}
              restartTurnstile={restartTurnstile}
            />
          ) : (
            <>
              <div className="east-tower-container">
                <div className="east-tower-bg" />
                <div className="east-title">{westLabel}</div>
                <div
                  className="tower-turnstile"
                  ref={westTurnstileRef}
                  style={{ zoom: towerScales.west }}
                >
                  {westTurnstiles.map((item) => (
                    <Turnstile
                      key={item.id}
                      content={item}
                      restartTurnstile={restartTurnstile}
                    />
                  ))}
                </div>
              </div>

              <div className="west-tower-container">
                <div className="west-tower-bg" />
                <div className="west-title">{eastLabel}</div>
                <div
                  className="tower-turnstile"
                  ref={eastTurnstileRef}
                  style={{ zoom: towerScales.east }}
                >
                  {eastTurnstiles.map((item) => (
                    <Turnstile
                      key={item.id}
                      content={item}
                      restartTurnstile={restartTurnstile}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {!isMobileView && <Footer type="half" style={{ background: 'transparent' }} />}
      </div>
    </HelmetProvider>
  );
};

const Turnstile = ({ content, restartTurnstile}) => {

  const canRestart = u.getPermissions('combined')?.['turnstile-restart'] === 2;
 
  const shouldShowLeft = content.double || content.type === 'left';
  const shouldShowRight = content.double || content.type === 'right';
 
  return (
    <div className={`turnstile-container ${content.action} ${content.result}`}>
      {/* Left Arm */}
      {shouldShowLeft && (
        <div className="left-arm">
          <div
            className={`turnstile-base-container ${
              content.type !== 'left' && content.double ? 'double' : ''
            }`}
          >
            <div className="turnstile-top">
              <div className="turnstile-sensor">
                <div className="turnstile-scanner" />
              </div>
            </div>
            <div className="turnstile-base" />
            <div className="turnstile-bottom">
              <div className="turnstile-sensor">
                <div className="turnstile-scanner" />
              </div>
            </div>
          </div>
          <div className="door">
            <div className="door-motor" />
            <div className="left-door" />
          </div>
        </div>
      )}
 
      {/* Right Arm */}
      {shouldShowRight && (
        <div className="right-arm">
          <div className="door">
            <div className="right-door" />
            <div className="door-motor" />
          </div>
          <div
            className={`turnstile-base-container ${
              content.type !== 'right' && content.double ? 'double' : ''
            }`}
          >
            <div className="turnstile-top">
              <div className="turnstile-sensor">
                <div className="turnstile-scanner" />
              </div>
            </div>
            <div className="turnstile-base" />
            <div className="turnstile-bottom">
              <div className="turnstile-sensor">
                <div className="turnstile-scanner" />
              </div>
            </div>
          </div>
        </div>
      )}
 
      {/* Movement Indicators */}
      <div
        className={`movement-container ${content.action} ${
          content.double ? 'double' : content.type
        } ${content.result}`}
      >
        <img
          className="movement-indicator success"
          src="/images/turnstile/down-arrow-green.png"
          alt="Success"
        />
        <img
          className="movement-indicator passback"
          src="/images/turnstile/cross-orange.png"
          alt="Passback"
        />
        <img
          className="movement-indicator failed"
          src="/images/turnstile/cross-red.png"
          alt="Failed"
        />
      </div>
 
      {/* Status Text */}
      <div
        className={`status-text ${content.type} ${
          content.double ? 'double' : ''
        }`}
      >
        <div className="status-line">
          <span className="name">{content.label}:</span>{' '}
          <span className="action-text">
            {content.result === 'Failed' || content.result === 'Passback'
              ? content.result
              : content.action}
          </span>
        </div>
        {/* {content.action !== 'Idle' &&
          content.action !== 'Offline' &&
          content.userType && <div className="user">{content.userType}</div>} */}

        {canRestart && (
          <button
            type="button"
            className="restart-icon-button"
            onClick={() => {
              restartTurnstile(content.id);
            }}
            aria-label="Restart"
          >
            <img src="/images/turnstile/restart.png" alt="Restart" />
          </button>
        )}
      </div>
    </div>
  );
};
 
// Mobile View Component: forced landscape, single wing selector, 5-across turnstile row
const MobileMonitoringView = ({
  westTurnstiles,
  eastTurnstiles,
  wing,
  onWingChange,
  rowScale,
  rowHeight,
  rowRef,
  containerRef,
  headerRef,
  footerRef,
  restartTurnstile,
}) => {
  const activeTurnstiles = wing === 'west' ? westTurnstiles : eastTurnstiles;

  return (
    <div className="mobile-container" ref={containerRef}>
      <div className="mobile-header-row" ref={headerRef}>
        <div className="building-toggle">
          {[{ label: 'East', value: 'east' }, { label: 'West', value: 'west' }].map((option) => (
            <div
              key={option.value}
              className={'building-toggle-option' + (wing === option.value ? ' selected' : '')}
              onClick={() => onWingChange(option.value)}
            >
              {option.label}
            </div>
          ))}
        </div>
      </div>

      <div
        className="mobile-turnstile-row-wrapper"
        style={rowHeight ? { height: rowHeight } : undefined}
      >
        <div
          className="mobile-turnstile-row"
          ref={rowRef}
          style={{ transform: `scale(${rowScale})` }}
        >
          {activeTurnstiles.map((item) => (
            <Turnstile
              key={item.id}
              content={item}
              restartTurnstile={restartTurnstile}
            />
          ))}
        </div>
      </div>

      <div className="mobile-footer-row" ref={footerRef}>
        <img
          className="mobile-logo"
          src="/images/app/app-logo-small.png"
          alt="164 Kallang"
        />
        <img
          className="mobile-footer-logo"
          src="/images/app/footer.png"
          alt="Vita Partners"
        />
      </div>
    </div>
  );
};

export default TurnstileMonitoring;